import type { MapTheme, Tile } from "@dailywars/core/engine";
import { DEFAULT_MAP_THEME } from "@dailywars/core/render/mapThemes";
import { isSeaShoreRenderAt } from "@dailywars/core/render/seaShoreLand";
import { resolveBakedSeaShoreOverlayLayerNames } from "@dailywars/core/render/seaShoreLayers";

import { loadCachedImage } from "#/game/cachedImage";

function seaShoreTileUrl({ theme, layerName }: { theme: MapTheme; layerName: string }): string {
  return `/terrain/${theme}/sea/${layerName}.png`;
}

function seaBaseTileUrl({ theme }: { theme: MapTheme }): string {
  return `/terrain/${theme}/sea/tile.png`;
}

export function getSeaTerrainImageSrc({
  theme,
  shoreLayer,
}: {
  theme?: MapTheme;
  shoreLayer?: string;
}): string {
  const selectedTheme = theme ?? DEFAULT_MAP_THEME;
  if (shoreLayer) return seaShoreTileUrl({ theme: selectedTheme, layerName: shoreLayer });
  return seaBaseTileUrl({ theme: selectedTheme });
}

export function getSeaTerrainImage({
  theme,
  shoreLayer,
}: {
  theme?: MapTheme;
  shoreLayer?: string;
}): HTMLImageElement {
  return loadCachedImage(getSeaTerrainImageSrc({ theme, shoreLayer }));
}

export function resolveSeaShoreImage({
  theme,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  theme?: MapTheme;
}): HTMLImageElement {
  return getSeaTerrainImage({ theme });
}

export function resolveSeaShoreOverlayImages({
  tiles,
  width,
  x,
  y,
  theme,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  theme?: MapTheme;
}): HTMLImageElement[] {
  return resolveBakedSeaShoreOverlayLayerNames({ tiles, width, x, y }).map((shoreLayer) =>
    getSeaTerrainImage({ theme, shoreLayer }),
  );
}

function collectSeaShoreUrls({
  tiles,
  width,
  height,
  theme,
}: {
  tiles: readonly Tile[];
  width: number;
  height: number;
  theme?: MapTheme;
}): string[] {
  const selectedTheme = theme ?? DEFAULT_MAP_THEME;
  const urls = new Set<string>([getSeaTerrainImageSrc({ theme: selectedTheme })]);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!isSeaShoreRenderAt({ tiles, width, x, y })) continue;
      for (const overlayLayer of resolveBakedSeaShoreOverlayLayerNames({ tiles, width, x, y })) {
        urls.add(getSeaTerrainImageSrc({ theme: selectedTheme, shoreLayer: overlayLayer }));
      }
    }
  }

  return [...urls];
}

export function preloadSeaShoreImages({
  tiles,
  width,
  height,
  theme,
}: {
  tiles: readonly Tile[];
  width: number;
  height: number;
  theme?: MapTheme;
}): HTMLImageElement[] {
  return collectSeaShoreUrls({ tiles, width, height, theme }).map((url) => loadCachedImage(url));
}
